// frontend/src/lib/branding.js
// #34 - shared constants for tenant custom CSS/branding, used by both the
// TenantBrandingPanel settings UI and the BrandingContext that applies it
// app-wide. Kept in sync with infrastructure/lambda/shared/branding.ts.

export const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/
export const MAX_CUSTOM_CSS_LENGTH = 20000

// Curated font list: a mix of already-loaded system fonts (no network
// request) and popular Google Fonts (loaded on demand via a <link> tag).
// "custom" lets a tenant type any Google Fonts family name directly.
//
// Font family names (Inter, Roboto, ...) are proper nouns and intentionally
// left untranslated, same as any font picker; the qualifier text around them
// ("Default (...)", "... (system)") is a real UI string and is translated via
// `labelKey`/`labelParams` (admin.json `settings.branding.fonts.*`) rather
// than hardcoded here - see TenantBrandingPanel.jsx for where it's resolved.
export const BRANDING_FONT_OPTIONS = [
  { value: '', labelKey: 'settings.branding.fonts.default', source: 'system' },
  { value: 'Inter', label: 'Inter', source: 'google' },
  { value: 'Roboto', label: 'Roboto', source: 'google' },
  { value: 'Open Sans', label: 'Open Sans', source: 'google' },
  { value: 'Lato', label: 'Lato', source: 'google' },
  { value: 'Montserrat', label: 'Montserrat', source: 'google' },
  { value: 'Poppins', label: 'Poppins', source: 'google' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro', source: 'google' },
  { value: 'Merriweather', label: 'Merriweather', source: 'google' },
  { value: 'Arial', labelKey: 'settings.branding.fonts.systemFont', labelParams: { font: 'Arial' }, source: 'system' },
  { value: 'Georgia', labelKey: 'settings.branding.fonts.systemFont', labelParams: { font: 'Georgia' }, source: 'system' },
  { value: 'Verdana', labelKey: 'settings.branding.fonts.systemFont', labelParams: { font: 'Verdana' }, source: 'system' },
]

const SYSTEM_FONT_VALUES = new Set(
  BRANDING_FONT_OPTIONS.filter((f) => f.source === 'system' && f.value).map((f) => f.value)
)

export function isSystemFont(fontFamily) {
  return SYSTEM_FONT_VALUES.has(fontFamily)
}

export function googleFontsHref(fontFamily) {
  const family = encodeURIComponent(fontFamily).replace(/%20/g, '+')
  return `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`
}

export const DEFAULT_BRANDING = {
  primaryColor: '',
  secondaryColor: '',
  backgroundColor: '',
  textColor: '',
  fontFamily: '',
  logoKey: null,
  logoUrl: null,
  customCss: '',
  hideBranding: false,
}

// CSS custom properties on :root that branding overrides. Centralized here
// so the settings-panel preview and the app-wide BrandingContext apply the
// exact same mapping.
export const BRANDING_CSS_VAR_MAP = {
  primaryColor: ['--color-primary'],
  secondaryColor: ['--color-secondary'],
  backgroundColor: ['--color-bg', '--color-bg-light'],
  textColor: ['--color-text'],
}

export function fontFamilyCssValue(fontFamily) {
  if (!fontFamily) return null
  const quoted = fontFamily.includes(' ') ? `"${fontFamily}"` : fontFamily
  return `${quoted}, Inter, system-ui, Avenir, Helvetica, Arial, sans-serif`
}
